import { PassiveSkill, PassiveSkillCompletion, PassiveSkillMilestone, Stats } from '../types';

// --- Date helpers (UTC-normalized) ---
const toUTCDate = (d: Date): Date => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

const getTodayDateString = (): string => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
};

const getYesterdayDateString = (): string => {
  const now = new Date();
  // Move back one day in UTC terms
  const y = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
  return `${y.getUTCFullYear()}-${String(y.getUTCMonth() + 1).padStart(2, '0')}-${String(y.getUTCDate()).padStart(2, '0')}`;
};

const parseYYYYMMDDToUTC = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, (month || 1) - 1, day || 1));
};

// Skill level progression thresholds
const LEVEL_THRESHOLDS = {
  1: 0,    // Starting level
  2: 7,    // 1 week
  3: 14,   // 2 weeks
  4: 30,   // 1 month
  5: 60,   // 2 months
  6: 90,   // 3 months
  7: 150,  // 5 months
  8: 200,  // ~7 months
  9: 270,  // 9 months
  10: 365  // 1 year
};

// Stat boost progression (percentage per level)
const STAT_BOOST_PER_LEVEL = {
  1: 0.005,  // 0.5%
  2: 0.01,   // 1%
  3: 0.015,  // 1.5%
  4: 0.02,   // 2%
  5: 0.025,  // 2.5%
  6: 0.03,   // 3%
  7: 0.04,   // 4%
  8: 0.05,   // 5%
  9: 0.07,   // 7%
  10: 0.10   // 10%
};

export const passiveSkillService = {
  /**
   * Calculate skill level based on total completions
   */
  calculateSkillLevel: (totalCompletions: number, targetDuration?: number): number => {
    // Use scaled thresholds if targetDuration is provided
    let thresholds: Record<number, number> = LEVEL_THRESHOLDS;
    if (targetDuration) {
      // For goal-based skills, distribute levels linearly across the target duration
      // Level 1: 0 completions, Level 10: targetDuration completions
      thresholds = {};
      for (let level = 1; level <= 10; level++) {
        if (level === 1) {
          thresholds[level] = 0; // Always start at level 1 with 0 completions
        } else if (level === 2) {
          thresholds[level] = 1; // Level 2 requires 1 completion (as requested)
        } else {
          // For very short goals (1-7 days), use minimal progression to prevent instant max level
          if (targetDuration <= 7) {
            // Cap at reasonable progression: Level 10 requires at least 7 completions for short goals
            const minTarget = Math.max(targetDuration, 7);
            const remainingLevels = 8; // Levels 3-10
            const remainingCompletions = minTarget - 1; // Subtract the 1 for level 2
            const progressRatio = (level - 2) / remainingLevels; // Progress from level 2 to 10
            thresholds[level] = 1 + Math.round(progressRatio * remainingCompletions);
          } else {
            // Normal linear distribution for longer goals
            const remainingLevels = 8; // Levels 3-10
            const remainingCompletions = targetDuration - 1; // Subtract the 1 for level 2
            const progressRatio = (level - 2) / remainingLevels; // Progress from level 2 to 10
            thresholds[level] = 1 + Math.round(progressRatio * remainingCompletions);
          }
        }
      }
      // Ensure level 10 threshold is exactly the target duration
      thresholds[10] = targetDuration;
    }

    let level = 1;
    for (let i = 10; i >= 1; i--) {
      if (totalCompletions >= thresholds[i]) {
        level = i;
        break;
      }
    }
    return level;
  },

  /**
   * Get stat boost percentage for a given level
   */
  getStatBoostForLevel: (level: number): number => {
    return STAT_BOOST_PER_LEVEL[level as keyof typeof STAT_BOOST_PER_LEVEL] || 0;
  },

  /**
   * Calculate current streak for a passive skill
   */
  calculateStreak: (skill: PassiveSkill): number => {
    const todayStr = getTodayDateString();
    const yesterdayStr = getYesterdayDateString();
    
    if (!skill.completionHistory || skill.completionHistory.length === 0) {
      return 0;
    }

    // Use a Set for O(1) membership checks
    const historySet = new Set(skill.completionHistory);
    
    // Determine starting point: today if done today, else yesterday if done yesterday, else none
    let cursor = historySet.has(todayStr)
      ? parseYYYYMMDDToUTC(todayStr)
      : historySet.has(yesterdayStr)
        ? parseYYYYMMDDToUTC(yesterdayStr)
        : null;
    if (!cursor) return 0;

    // For daily skills, count consecutive days
    if (skill.frequency === 'daily') {
      let streak = 0;
      // iterate backwards day-by-day in UTC
      while (true) {
        const candidate = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}-${String(cursor.getUTCDate()).padStart(2, '0')}`;
        if (historySet.has(candidate)) {
          streak++;
          cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), cursor.getUTCDate() - 1));
        } else {
          break;
        }
      }
      return streak;
    }

    // For weekly/custom, count consecutive weeks
    const sortedHistory = [...historySet].sort().reverse();
    return passiveSkillService.calculateWeeklyStreak(
      sortedHistory,
      skill.targetDaysPerWeek || 1,
      skill.allowedWeekdays
    );
  },

  /**
   * Calculate weekly streak (consecutive weeks meeting target)
   */
  calculateWeeklyStreak: (completionHistory: string[], targetDaysPerWeek: number, allowedWeekdays?: number[]): number => {
    const today = toUTCDate(new Date());
    let weekStreak = 0;
    let currentWeekStart = new Date(today);
    currentWeekStart = new Date(Date.UTC(currentWeekStart.getUTCFullYear(), currentWeekStart.getUTCMonth(), currentWeekStart.getUTCDate() - currentWeekStart.getUTCDay())); // Sunday
    
    for (let weekOffset = 0; weekOffset < 52; weekOffset++) {
      const weekStart = new Date(Date.UTC(currentWeekStart.getUTCFullYear(), currentWeekStart.getUTCMonth(), currentWeekStart.getUTCDate() - (weekOffset * 7)));
      const weekEnd = new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + 6, 23, 59, 59, 999));
      
      const completionsThisWeek = completionHistory.filter(dateStr => {
        const date = parseYYYYMMDDToUTC(dateStr);
        const inWindow = date >= weekStart && date <= weekEnd;
        if (!inWindow) return false;
        if (allowedWeekdays && allowedWeekdays.length > 0) {
          const weekday = date.getUTCDay();
          return allowedWeekdays.includes(weekday);
        }
        return true;
      }).length;
      
      const targetForWeek = allowedWeekdays && allowedWeekdays.length > 0 ? allowedWeekdays.length : targetDaysPerWeek;
      if (completionsThisWeek >= targetForWeek) {
        weekStreak++;
      } else {
        break; // Streak broken
      }
    }
    
    return weekStreak;
  },

  /**
   * Check if a skill can be completed today
   */
  canCompleteToday: (skill: PassiveSkill): boolean => {
    const today = getTodayDateString();
    // Disallow duplicate completion for today
    if (skill.completionHistory.includes(today)) return false;
    // If custom with allowed weekdays, only allow on scheduled days
    if (skill.frequency === 'custom' && skill.allowedWeekdays && skill.allowedWeekdays.length > 0) {
      const weekday = new Date().getUTCDay();
      if (!skill.allowedWeekdays.includes(weekday)) return false;
    }
    return true;
  },

  /**
   * Mark skill as completed for today
   */
  completeSkill: (skill: PassiveSkill): {
    updatedSkill: PassiveSkill;
    leveledUp: boolean;
    milestone: PassiveSkillMilestone | null;
  } => {
    const today = getTodayDateString();
    
    // If custom and today is not an allowed weekday, ignore completion (no-op)
    if (skill.frequency === 'custom' && skill.allowedWeekdays && skill.allowedWeekdays.length > 0) {
      const weekday = new Date().getUTCDay();
      if (!skill.allowedWeekdays.includes(weekday)) {
        return { updatedSkill: skill, leveledUp: false, milestone: null };
      }
    }

    // Prevent duplicates if already completed today
    if (skill.completionHistory.includes(today)) {
      return { updatedSkill: skill, leveledUp: false, milestone: null };
    }
    
    // Add today to completion history (keep last 365 days only)
    const newHistory = [...skill.completionHistory, today]
      .filter((date, index, self) => self.indexOf(date) === index) // Remove duplicates
      .sort()
      .slice(-365); // Keep only last 365 days
    
    const newTotalCompletions = skill.totalCompletions + 1;
    const newLevel = passiveSkillService.calculateSkillLevel(newTotalCompletions, skill.targetDuration);
    const leveledUp = newLevel > skill.level;
    
    // Calculate new streak
    const newSkill: PassiveSkill = {
      ...skill,
      completionHistory: newHistory,
      lastCompletedDate: today,
      totalCompletions: newTotalCompletions,
      level: newLevel,
      statBoost: {
        ...skill.statBoost,
        percentBoost: passiveSkillService.getStatBoostForLevel(newLevel)
      }
    };
    
    newSkill.currentStreak = passiveSkillService.calculateStreak(newSkill);
    newSkill.longestStreak = Math.max(newSkill.currentStreak, newSkill.longestStreak);
    
    // Check for milestones
    const milestone = passiveSkillService.checkMilestone(newSkill, skill);
    
    return {
      updatedSkill: newSkill,
      leveledUp,
      milestone
    };
  },

  /**
   * Check if a milestone was reached
   */
  checkMilestone: (newSkill: PassiveSkill, oldSkill: PassiveSkill): PassiveSkillMilestone | null => {
    // Streak milestones
    const streakMilestones = [7, 14, 30, 60, 90, 180, 365];
    for (const milestone of streakMilestones) {
      if (newSkill.currentStreak === milestone && oldSkill.currentStreak !== milestone) {
        return {
          skillId: newSkill.id,
          type: 'streak',
          value: milestone,
          reachedAt: Date.now(),
          reward: passiveSkillService.getStreakReward(milestone)
        };
      }
    }
    
    // Level milestones
    if (newSkill.level > oldSkill.level) {
      return {
        skillId: newSkill.id,
        type: 'level',
        value: newSkill.level,
        reachedAt: Date.now(),
        reward: passiveSkillService.getLevelReward(newSkill.level)
      };
    }
    
    // Total completion milestones
    const completionMilestones = [10, 25, 50, 100, 200, 365, 500, 1000];
    for (const milestone of completionMilestones) {
      if (newSkill.totalCompletions === milestone) {
        return {
          skillId: newSkill.id,
          type: 'total',
          value: milestone,
          reachedAt: Date.now(),
          reward: passiveSkillService.getCompletionReward(milestone)
        };
      }
    }
    
    return null;
  },

  /**
   * Get reward for streak milestone
   */
  getStreakReward: (streak: number): PassiveSkillMilestone['reward'] => {
    const rewards: Record<number, PassiveSkillMilestone['reward']> = {
      7: { xp: 50, currency: 25 },
      14: { xp: 100, currency: 50 },
      30: { xp: 200, currency: 100, statPoints: 1 },
      60: { xp: 400, currency: 200, statPoints: 2 },
      90: { xp: 600, currency: 300, statPoints: 3 },
      180: { xp: 1000, currency: 500, statPoints: 5, title: 'Disciplined Hunter' },
      365: { xp: 2000, currency: 1000, statPoints: 10, title: 'Master of Routine' }
    };
    return rewards[streak] || { xp: 50, currency: 25 };
  },

  /**
   * Get reward for level up
   */
  getLevelReward: (level: number): PassiveSkillMilestone['reward'] => {
    return {
      xp: level * 100,
      currency: level * 50,
      statPoints: level >= 5 ? Math.floor(level / 2) : 0
    };
  },

  /**
   * Get reward for completion milestone
   */
  getCompletionReward: (completions: number): PassiveSkillMilestone['reward'] => {
    const rewards: Record<number, PassiveSkillMilestone['reward']> = {
      10: { xp: 100, currency: 50 },
      25: { xp: 250, currency: 125 },
      50: { xp: 500, currency: 250 },
      100: { xp: 1000, currency: 500, statPoints: 3 },
      200: { xp: 2000, currency: 1000, statPoints: 5 },
      365: { xp: 3650, currency: 1825, statPoints: 10 },
      500: { xp: 5000, currency: 2500, statPoints: 15 },
      1000: { xp: 10000, currency: 5000, statPoints: 25, title: 'Legendary Discipline' }
    };
    return rewards[completions] || { xp: 100, currency: 50 };
  },

  /**
   * Calculate total passive stat boosts from all active skills
   */
  calculateTotalPassiveBoosts: (skills: PassiveSkill[]): Partial<Stats> => {
    const boosts: Partial<Stats> = {
      strength: 0,
      intelligence: 0,
      agility: 0,
      vitality: 0,
      sense: 0
    };
    
    skills.filter(skill => skill.isActive).forEach(skill => {
      const statKey = skill.statBoost.stat;
      boosts[statKey] = (boosts[statKey] || 0) + skill.statBoost.percentBoost;
    });
    
    return boosts;
  },

  /**
   * Apply passive boosts to base stats
   */
  applyPassiveBoosts: (baseStats: Stats, boosts: Partial<Stats>): Stats => {
    return {
      strength: Math.floor(baseStats.strength * (1 + (boosts.strength || 0))),
      intelligence: Math.floor(baseStats.intelligence * (1 + (boosts.intelligence || 0))),
      agility: Math.floor(baseStats.agility * (1 + (boosts.agility || 0))),
      vitality: Math.floor(baseStats.vitality * (1 + (boosts.vitality || 0))),
      sense: Math.floor(baseStats.sense * (1 + (boosts.sense || 0)))
    };
  },

  /**
   * Get completion rate for the current week
   */
  getWeeklyCompletionRate: (skill: PassiveSkill): { completed: number; target: number; rate: number } => {
    const today = toUTCDate(new Date());

    // For daily-frequency skills, show a rolling 7-day window so 7 straight days
    // always appears as 7/7 even across calendar week boundaries.
    if (skill.frequency === 'daily') {
      const rollingStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 6));

      const completionsLast7Days = skill.completionHistory.filter(dateStr => {
        const date = parseYYYYMMDDToUTC(dateStr);
        return date >= rollingStart && date <= today;
      }).length;

      const target = 7;
      const rate = target > 0 ? completionsLast7Days / target : 0;

      return {
        completed: Math.min(completionsLast7Days, target),
        target,
        rate: Math.min(rate, 1)
      };
    }

    // Weekly/custom frequency uses calendar week (Sunday–Saturday)
    const weekStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - today.getUTCDay()));

    const weekEnd = new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + 6, 23, 59, 59, 999));

    const completionsThisWeek = skill.completionHistory.filter(dateStr => {
      const date = parseYYYYMMDDToUTC(dateStr);
      const inWindow = date >= weekStart && date <= weekEnd;
      if (!inWindow) return false;
      if (skill.frequency === 'custom' && skill.allowedWeekdays && skill.allowedWeekdays.length > 0) {
        const weekday = date.getUTCDay();
        return skill.allowedWeekdays.includes(weekday);
      }
      return true;
    }).length;

    const target = skill.frequency === 'custom'
      ? (skill.allowedWeekdays && skill.allowedWeekdays.length > 0 ? skill.allowedWeekdays.length : (skill.targetDaysPerWeek || 1))
      : (skill.targetDaysPerWeek || 1);
    const rate = target > 0 ? completionsThisWeek / target : 0;

    return {
      completed: Math.min(completionsThisWeek, target),
      target,
      rate: Math.min(rate, 1)
    };
  },

  /**
   * Get suggested icon for a category
   */
  getDefaultIconForCategory: (category: string): string => {
    const icons: Record<string, string> = {
      physical: '⚔️',
      mental: '📚',
      social: '🤝',
      spiritual: '🧘',
      productivity: '⚡'
    };
    return icons[category] || '⭐';
  }
};
